import React, { Fragment } from "react";
import { Modal } from "@fluentui/react";
import * as S from "../Styles/RecordsModalStyles";
import { ChevronDownIcon, ChevronUpIcon } from "@fluentui/react-icons-mdl2";

interface IRecord
{
    id: string;
    fecha: string;
    composicionCartera: string;
    portfolioModelName?: string;
}

interface IRecordsModalProps
{
    isOpen: boolean;
    onClose: () => void;
    onSelect: (selectedRecord: IRecord) => void;
    records: IRecord[];
    portfolioModelName?: string;
    showPortfolioColumn?: boolean;
}

const RecordsModal: React.FC<IRecordsModalProps> = ({isOpen, onClose, onSelect, records, portfolioModelName, showPortfolioColumn = false}) =>
{
    const [selectedRecord, setSelectedRecord] = React.useState<IRecord | null>(null);
    const [expandedPortfolios, setExpandedPortfolios] = React.useState<{[key: string]: boolean}>({});

    // Limpiar selección al cerrar el modal
    React.useEffect(() =>
    {
        if(!isOpen)
        {
            setSelectedRecord(null);
        }
    }, [isOpen]);

    // Inicializar todas las carteras como expandidas cuando cambian los datos
    React.useEffect(() =>
    {
        if(showPortfolioColumn && records.length > 0)
        {
            const portfolioNames = Array.from(new Set(records.map(item => item.portfolioModelName).filter(Boolean)));
            const initialExpanded: {[key: string]: boolean} = {};
            portfolioNames.forEach(name =>
            {
                if(name)
                {
                    initialExpanded[name] = true;
                }
            });
            setExpandedPortfolios(initialExpanded);
        }
    }, [records, showPortfolioColumn]);

    const togglePortfolio = React.useCallback((portfolioName: string) =>
    {
        setExpandedPortfolios(prev =>
        ({
            ...prev,
            [portfolioName]: !prev[portfolioName]
        }));
    }, []);

    const handleClose = React.useCallback(() =>
    {
        setSelectedRecord(null);
        onClose();
    }, [onClose]);

    const handleSelect = React.useCallback(() =>
    {
        if(selectedRecord)
        {
            onSelect(selectedRecord);
            handleClose();
        }
    }, [selectedRecord, onSelect, handleClose]);

    const handleRowClick = React.useCallback((record: IRecord) =>
    {
        setSelectedRecord(record);
    }, []);

    const modalTitle = showPortfolioColumn 
        ? "Composiciones de las Carteras Modelo"
        : portfolioModelName 
            ? `Composiciones de la Cartera Modelo: ${portfolioModelName}`
            : "Composiciones de la Cartera Modelo";

    if(showPortfolioColumn)
    {
        const groupedData = records.reduce((acc, item) =>
        {
            const portfolioName = item.portfolioModelName || 'Sin nombre';
            if(!acc[portfolioName])
            {
                acc[portfolioName] = [];
            }
            acc[portfolioName].push(item);
            return acc;
        }, {} as {[key: string]: IRecord[]});

        const hasMultiplePortfolios = Object.keys(groupedData).length > 1;

        return(
            <Modal isOpen={isOpen} onDismiss={handleClose} isBlocking={true} containerClassName="modal-container" styles={{main: {width: '60vw'}}}>
                <S.ModalContainer>
                    <S.ModalTitle>{modalTitle}</S.ModalTitle>
                    <S.TableContainer>
                        <S.Table $hasMultiplePortfolios={hasMultiplePortfolios}>
                            <thead>
                                <tr>
                                    <th>Cartera Modelo / Fecha</th>
                                    <th>Composición de la Cartera</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(groupedData).map(([portfolioName, portfolioRecords]) =>
                                (
                                    <Fragment key={portfolioName}>
                                        <tr onClick={() => togglePortfolio(portfolioName)}
                                            style={
                                            { 
                                                backgroundColor: '#f8f9fa',
                                                cursor: 'pointer',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            <td style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                {expandedPortfolios[portfolioName] ? 
                                                    <ChevronUpIcon style={{fontSize: '14px'}} /> : 
                                                    <ChevronDownIcon style={{fontSize: '14px'}} />
                                                }
                                                <span>{portfolioName}</span>
                                                <span>({portfolioRecords.length} registros)</span>
                                            </td>
                                            <td></td>
                                        </tr>
                                        {expandedPortfolios[portfolioName] && portfolioRecords.map((record) =>
                                        (
                                            <S.TableRow key={record.id} onClick={() => handleRowClick(record)} $isSelected={selectedRecord?.id === record.id}>
                                                <td style={{ paddingLeft: '2rem' }}>{record.fecha}</td>
                                                <td>{record.composicionCartera}</td>
                                            </S.TableRow>
                                        ))}
                                    </Fragment>
                                ))}
                            </tbody>
                        </S.Table>
                    </S.TableContainer>
                    <S.ButtonContainer>
                        <S.CloseButton onClick={handleClose}>
                            Cerrar
                        </S.CloseButton>
                        <S.SelectButton onClick={handleSelect} disabled={!selectedRecord}>
                            Seleccionar
                        </S.SelectButton>
                    </S.ButtonContainer>
                </S.ModalContainer>
            </Modal>
        );
    }

    return(
        <Modal isOpen={isOpen} onDismiss={handleClose} isBlocking={true} containerClassName="modal-container" styles={{main: {width: '45vw'}}}>
            <S.ModalContainer>
                <S.ModalTitle>{modalTitle}</S.ModalTitle>
                <S.TableContainer>
                    <S.Table>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Composición de la Cartera</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((record) =>
                            (
                                <S.TableRow key={record.id} onClick={() => handleRowClick(record)} $isSelected={selectedRecord?.id === record.id}>
                                    <td>{record.fecha}</td>
                                    <td>{record.composicionCartera}</td>
                                </S.TableRow>
                            ))}
                        </tbody>
                    </S.Table>
                </S.TableContainer>
                <S.ButtonContainer>
                    <S.CloseButton onClick={handleClose}>
                        Cerrar
                    </S.CloseButton>
                    <S.SelectButton onClick={handleSelect} disabled={!selectedRecord}>
                        Seleccionar
                    </S.SelectButton>
                </S.ButtonContainer>
            </S.ModalContainer>
        </Modal>
    );
};

export default RecordsModal;